/**
 * DisplayFeature - Concrete class implementing IDisplayFeature.
 *
 * Encapsulates the identity, type, data, and mutable visualization state
 * of a displayable element. Created by workflow services and registered
 * with DisplayFeatureManager.
 *
 * @example
 * ```typescript
 * const feature = new DisplayFeature({
 *     id: 'evar-centerlines',
 *     type: DisplayFeatureType.CENTERLINES,
 *     data: centerlines,
 *     properties: { tubeDiameter: 0.4, activeTubeDiameter: 0.8 },
 * });
 *
 * displayManager.addFeature(feature);
 * ```
 */

import type { DisplayFeatureType, DisplayPropertiesMap, IDisplayFeature, DisplayDataFor } from './types';
import { ViewMode } from './types';

export interface DisplayFeatureOptions<
    T extends DisplayFeatureType = DisplayFeatureType,
    TData = DisplayDataFor<T>,
> {
    /** Unique identifier (e.g., 'evar-ct-volume', 'evar-centerline-main') */
    id: string;
    /** Feature type determines which DisplayObject can render it */
    type: T;
    /** Reference to domain data (Volume, Centerline, etc.) */
    data: TData;
    /** Visualization properties */
    properties: DisplayPropertiesMap[T];
    /** Whether initially visible (defaults to true) */
    visible?: boolean;
}

const ALL_VIEW_MODES = Object.values(ViewMode);

function buildVisibilityMap(visible: boolean): Map<ViewMode, boolean> {
    const map = new Map<ViewMode, boolean>();
    for (const m of ALL_VIEW_MODES) map.set(m, visible);
    return map;
}

export class DisplayFeature<
    T extends DisplayFeatureType = DisplayFeatureType,
    TData = DisplayDataFor<T>,
> implements IDisplayFeature<T, TData> {
    readonly id: string;
    readonly type: T;
    data: TData;
    properties: DisplayPropertiesMap[T];
    viewModeVisibility: Map<ViewMode, boolean>;

    constructor(options: DisplayFeatureOptions<T, TData>) {
        this.id = options.id;
        this.type = options.type;
        this.data = options.data;
        this.properties = options.properties;
        this.viewModeVisibility = buildVisibilityMap(options.visible ?? true);
    }
}
